import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Tag, Sparkles, Copy, Check } from "lucide-react";
import { toast } from "sonner";

export default function PromotionsSection() {
    const [promos, setPromos] = useState([]);
    const [copied, setCopied] = useState(null);

    useEffect(() => {
        api.get("/promotions", { params: { only_active: true } })
            .then((r) => setPromos(r.data))
            .catch(() => setPromos([]));
    }, []);

    if (promos.length === 0) return null;

    function copyCode(code) {
        navigator.clipboard.writeText(code);
        setCopied(code);
        toast.success("Código copiado");
        setTimeout(() => setCopied(null), 2000);
    }

    function isActive(p) {
        const now = new Date();
        if (p.starts_at && new Date(p.starts_at) > now) return false;
        if (p.ends_at && new Date(p.ends_at) < now) return false;
        return true;
    }

    const visible = promos.filter(isActive);
    if (visible.length === 0) return null;

    return (
        <section className="px-6 md:px-12 lg:px-20 py-16 lg:py-24" data-testid="promotions-section">
            <div className="text-center mb-12">
                <Sparkles strokeWidth={1.5} className="w-6 h-6 text-gold mx-auto" />
                <p className="overline mt-3">Promociones activas</p>
                <h2 className="font-serif text-4xl sm:text-5xl text-charcoal mt-3">
                    Aprovecha <span className="italic">ahora.</span>
                </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {visible.map((p) => (
                    <div
                        key={p.id}
                        className="bg-white border border-border-soft rounded-2xl p-6 flex flex-col gap-4"
                        data-testid={`promo-${p.id}`}
                    >
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <Tag strokeWidth={1.5} className="w-5 h-5 text-gold" />
                                <h3 className="font-serif text-2xl text-charcoal mt-2 leading-tight">
                                    {p.name}
                                </h3>
                                {p.description && (
                                    <p className="text-sm text-taupe mt-2 leading-relaxed">
                                        {p.description}
                                    </p>
                                )}
                            </div>
                            {p.discount_percent > 0 && (
                                <div className="text-right shrink-0">
                                    <div className="font-serif text-4xl text-gold leading-none">
                                        {Math.round(p.discount_percent)}%
                                    </div>
                                    <div className="overline mt-1">OFF</div>
                                </div>
                            )}
                        </div>
                        {p.ends_at && (
                            <p className="text-xs text-taupe">
                                Válido hasta {new Date(p.ends_at).toLocaleDateString("es-ES", { day: "numeric", month: "long" })}
                            </p>
                        )}
                        {p.code && (
                            <button
                                onClick={() => copyCode(p.code)}
                                className="flex items-center justify-between gap-3 bg-blush border-2 border-dashed border-gold rounded-lg px-4 py-3 hover:bg-blush/70 transition group"
                                data-testid={`promo-copy-${p.id}`}
                            >
                                <span className="text-xs tracking-wider uppercase text-taupe">Código</span>
                                <span className="font-mono font-bold text-gold tracking-widest">{p.code}</span>
                                {copied === p.code ? (
                                    <Check strokeWidth={1.5} className="w-4 h-4 text-gold" />
                                ) : (
                                    <Copy strokeWidth={1.5} className="w-4 h-4 text-taupe group-hover:text-gold" />
                                )}
                            </button>
                        )}
                        <Link
                            to="/catalogo"
                            className="mt-auto text-xs tracking-[0.2em] uppercase text-gold hover:text-gold-dark"
                        >
                            Comprar →
                        </Link>
                    </div>
                ))}
            </div>
        </section>
    );
}
